import { isFreshSample, type FusedPosition, type PositionSample, type PositionSource } from "./position";

/** Ground speed at which the aircraft counts as moving (taxi, block time). */
export const GROUND_MOVING_KT = 3;
/** Right after a takeoff-phase sample, slower ground contact is a landing; only roll speed stays departure-side (RTO). */
const AFTER_AIRBORNE_TAXI_KT = 22;

/**
 * Aircraft phase / state — source of truth for UI and later assignment.
 * Derive here (`deriveFlightPhase` / `aircraftSimState`). Do not re-implement
 * in views. Assignment owns inbox / canAssign / accept-decline; it should
 * *read* this snapshot, not invent a second phase machine.
 *
 * `no_sim` / `unknown` are off-bar: we never invent taxi/cruise from SI or last-known.
 */
export type FlightPhaseId =
  | "no_sim"
  | "unknown"
  | "on_ground"
  | "taxi"
  | "takeoff"
  | "climb"
  | "cruise"
  | "descent"
  | "approach"
  | "landed"
  | "parked";

export const FLIGHT_PHASE_IDS: readonly FlightPhaseId[] = [
  "no_sim",
  "unknown",
  "on_ground",
  "taxi",
  "takeoff",
  "climb",
  "cruise",
  "descent",
  "approach",
  "landed",
  "parked",
];

export const FLIGHT_PHASE_STEPS: Exclude<FlightPhaseId, "no_sim" | "unknown">[] = [
  "on_ground",
  "taxi",
  "takeoff",
  "climb",
  "cruise",
  "descent",
  "approach",
  "landed",
  "parked",
];

export type FlightPhaseView = {
  id: FlightPhaseId;
  label: string;
  shortLabel: string;
  /** 0–8 when derived from live SimConnect; -1 otherwise. */
  step: number;
  total: number;
  fromSim: boolean;
  note: string;
  /** 0–1 position inside the current step from live kinematics. */
  fraction: number;
};

/**
 * Rail bands for fill / plane. Not equal 1/8ths — cruise owns the long
 * middle so the marker keeps moving with remaining NM instead of sitting
 * on the Crz tick. Phase labels stay the 9-step set.
 */
export const PHASE_PROGRESS_BANDS: Record<(typeof FLIGHT_PHASE_STEPS)[number], { start: number; end: number }> = {
  on_ground: { start: 0, end: 0.07 },
  taxi: { start: 0.07, end: 0.13 },
  takeoff: { start: 0.13, end: 0.2 },
  climb: { start: 0.2, end: 0.3 },
  cruise: { start: 0.3, end: 0.72 },
  descent: { start: 0.72, end: 0.82 },
  approach: { start: 0.82, end: 0.9 },
  landed: { start: 0.9, end: 0.96 },
  parked: { start: 0.96, end: 1 },
};

/** Airborne route progress maps onto this span (climb start → approach end). */
export const PHASE_AIR_PROGRESS = {
  start: PHASE_PROGRESS_BANDS.climb.start,
  end: PHASE_PROGRESS_BANDS.approach.end,
} as const;

export type FlightProgressRoute = {
  remainingNm?: number | null;
  totalNm?: number | null;
};

/**
 * Serializable snapshot assignment (and the desk) should consume.
 * `sample` is the live SimConnect JSON when `live` is true; otherwise null.
 */
export type AircraftSimState = {
  phase: FlightPhaseId;
  live: boolean;
  source: PositionSource | null;
  sample: PositionSample | null;
  atUtc: string | null;
  note: string;
};

/** Native SimConnect datums → JSON keys on `PositionSample` / `AircraftSimState.sample`. */
export const SIMCONNECT_AIRCRAFT_FIELDS = [
  { sim: "PLANE LATITUDE", units: "degrees", json: "lat" },
  { sim: "PLANE LONGITUDE", units: "degrees", json: "lon" },
  { sim: "PLANE HEADING DEGREES TRUE", units: "degrees", json: "heading" },
  { sim: "PLANE ALTITUDE", units: "feet", json: "altitudeFt" },
  { sim: "PLANE ALT ABOVE GROUND", units: "feet", json: "altitudeAgl" },
  { sim: "AIRSPEED INDICATED", units: "knots", json: "airspeed" },
  { sim: "SIM ON GROUND", units: "bool", json: "onGround" },
  { sim: "GROUND VELOCITY", units: "knots", json: "groundSpeed" },
  { sim: "VERTICAL SPEED", units: "feet per minute", json: "verticalSpeedFpm" },
  { sim: "TRANSPONDER CODE:1", units: "BCO16", json: "squawk" },
] as const;

/** Optional reliable extras. Never oil / hydraulic quantity. */
export const SIMCONNECT_TELEMETRY_FIELDS = [
  { sim: "FUEL TOTAL QUANTITY WEIGHT", units: "pounds", json: "fuelQtyLb" },
  { sim: "GENERAL ENG COMBUSTION:1", units: "bool", json: "eng1Combustion" },
  { sim: "GENERAL ENG COMBUSTION:2", units: "bool", json: "eng2Combustion" },
  { sim: "GENERAL ENG COMBUSTION:3", units: "bool", json: "eng3Combustion" },
  { sim: "GENERAL ENG COMBUSTION:4", units: "bool", json: "eng4Combustion" },
  { sim: "TURB ENG N1:1", units: "percent", json: "eng1N1" },
  { sim: "TURB ENG N1:2", units: "percent", json: "eng2N1" },
  { sim: "APU PCT RPM", units: "percent", json: "apuPctRpm" },
  { sim: "ENG FUEL FLOW PPH:1", units: "pounds per hour", json: "eng1FlowPph" },
  { sim: "ENG FUEL FLOW PPH:2", units: "pounds per hour", json: "eng2FlowPph" },
  { sim: "ENG FUEL FLOW PPH:3", units: "pounds per hour", json: "eng3FlowPph" },
  { sim: "ENG FUEL FLOW PPH:4", units: "pounds per hour", json: "eng4FlowPph" },
  { sim: "UNLIMITED FUEL", units: "bool", json: "unlimitedFuel" },
  { sim: "FUEL DUMP ACTIVE", units: "bool", json: "fuelDumpActive" },
  { sim: "FUEL TANK CENTER QUANTITY", units: "pounds", json: "tankCenterLb" },
  { sim: "FUEL TANK LEFT MAIN QUANTITY", units: "pounds", json: "tankLeftMainLb" },
  { sim: "FUEL TANK RIGHT MAIN QUANTITY", units: "pounds", json: "tankRightMainLb" },
  { sim: "FUEL TANK LEFT AUX QUANTITY", units: "pounds", json: "tankLeftAuxLb" },
  { sim: "FUEL TANK RIGHT AUX QUANTITY", units: "pounds", json: "tankRightAuxLb" },
  { sim: "GEAR HANDLE POSITION", units: "bool", json: "gearHandleDown" },
  { sim: "GEAR POSITION:1", units: "percent", json: "gearPositionPct" },
] as const;

export function isFlightPhaseId(value: unknown): value is FlightPhaseId {
  return typeof value === "string" && (FLIGHT_PHASE_IDS as readonly string[]).includes(value);
}

export function hydrateFlightPhaseId(raw?: unknown): FlightPhaseId | null {
  return isFlightPhaseId(raw) ? raw : null;
}

export function phaseIsAirborne(id: FlightPhaseId): boolean {
  return id === "takeoff" || id === "climb" || id === "cruise" || id === "descent" || id === "approach";
}

export function phaseIsOnSurface(id: FlightPhaseId): boolean {
  return id === "on_ground" || id === "taxi" || id === "landed" || id === "parked";
}

/** Touchdown through taxi-in / parked — never map these back to departure ground. */
export function phaseIsArrived(id: FlightPhaseId): boolean {
  return id === "landed" || id === "parked";
}

const TOTAL = FLIGHT_PHASE_STEPS.length;

const LABEL: Record<FlightPhaseId, string> = {
  no_sim: "No sim",
  unknown: "Unknown",
  on_ground: "On ground",
  taxi: "Taxi",
  takeoff: "Takeoff",
  climb: "Climb",
  cruise: "Cruise",
  descent: "Descent",
  approach: "Approach",
  landed: "Landed",
  parked: "Ground",
};

const SHORT: Record<FlightPhaseId, string> = {
  no_sim: "PHASE —",
  unknown: "PHASE ?",
  on_ground: "PHASE GND",
  taxi: "PHASE TAXI",
  takeoff: "PHASE TO",
  climb: "PHASE CLB",
  cruise: "PHASE CRZ",
  descent: "PHASE DES",
  approach: "PHASE APP",
  landed: "PHASE LDG",
  parked: "PHASE IN",
};

/** Compact labels for the 9-tick ops track. */
const STEP_LABEL: Record<(typeof FLIGHT_PHASE_STEPS)[number], string> = {
  on_ground: "Out",
  taxi: "Taxi",
  takeoff: "TO",
  climb: "Clb",
  cruise: "Crz",
  descent: "Des",
  approach: "App",
  landed: "Land",
  parked: "Gnd",
};

const AIRBORNE: ReadonlySet<FlightPhaseId> = new Set(["takeoff", "climb", "cruise", "descent", "approach"]);

/** Level-off after a descent stays Descent until VS is a real climb. */
export const DESCENT_LEVELOFF_CLIMB_FPM = 400;

/** Within this NM of arrival, the phase bar shows Approach (airborne only). */
export const APPROACH_OVERRIDE_NM = 10;

export function flightPhaseLabel(id: FlightPhaseId): string {
  return LABEL[id];
}

export function flightPhaseShortLabel(id: FlightPhaseId): string {
  return SHORT[id];
}

export function flightPhaseStepLabel(id: (typeof FLIGHT_PHASE_STEPS)[number]): string {
  return STEP_LABEL[id];
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/**
 * Intra-step 0–1 from live kinematics. Cruise stays 0 here — route remaining
 * drives cruise (and the rest of the airborne span) in {@link flightPhaseTrackT}.
 */
export function flightPhaseFraction(
  id: FlightPhaseId,
  sample?: Pick<PositionSample, "groundSpeed" | "airspeed" | "altitudeAgl" | "verticalSpeedFpm" | "onGround"> | null,
): number {
  if (!sample) return 0;
  const speed = sampleSpeedKt(sample);
  const agl = typeof sample.altitudeAgl === "number" && Number.isFinite(sample.altitudeAgl) ? sample.altitudeAgl : null;
  const vs =
    typeof sample.verticalSpeedFpm === "number" && Number.isFinite(sample.verticalSpeedFpm)
      ? sample.verticalSpeedFpm
      : null;

  switch (id) {
    case "on_ground":
      return speed != null ? clamp01(speed / GROUND_MOVING_KT) : 0;
    case "taxi":
      return speed != null ? clamp01((speed - GROUND_MOVING_KT) / (55 - GROUND_MOVING_KT)) : 0;
    case "takeoff":
      if (sample.onGround === true && speed != null) return clamp01((speed - 55) / 60);
      if (agl != null) return clamp01(agl / 400);
      return 0;
    case "climb":
      if (agl != null) return clamp01((agl - 400) / 7600);
      if (vs != null && vs > 0) return clamp01(vs / 1800);
      return 0;
    case "cruise":
      return 0;
    case "descent":
      if (agl != null) return clamp01(1 - (agl - 1800) / 14000);
      if (vs != null && vs < 0) return clamp01(Math.abs(vs) / 1800);
      return 0;
    case "approach":
      if (agl != null) return clamp01(1 - agl / 1800);
      return 0;
    case "landed":
      if (speed != null) return clamp01(1 - Math.max(0, speed - 8) / 90);
      return 0.45;
    case "parked":
      if (speed != null && speed >= 8) return clamp01(0.25 + (1 - Math.min(speed, 40) / 40) * 0.6);
      return 1;
    default:
      return 0;
  }
}

function view(id: FlightPhaseId, fromSim: boolean, note: string, sample?: PositionSample): FlightPhaseView {
  const step = FLIGHT_PHASE_STEPS.indexOf(id as (typeof FLIGHT_PHASE_STEPS)[number]);
  return {
    id,
    label: LABEL[id],
    shortLabel: SHORT[id],
    step,
    total: TOTAL,
    fromSim,
    note,
    fraction: fromSim ? flightPhaseFraction(id, sample) : 0,
  };
}

function phaseIdFromTrack(
  phase: Pick<FlightPhaseView, "step"> & { id?: FlightPhaseId },
): (typeof FLIGHT_PHASE_STEPS)[number] | null {
  if (phase.id && FLIGHT_PHASE_STEPS.includes(phase.id as (typeof FLIGHT_PHASE_STEPS)[number])) {
    return phase.id as (typeof FLIGHT_PHASE_STEPS)[number];
  }
  if (phase.step >= 0 && phase.step < FLIGHT_PHASE_STEPS.length) {
    return FLIGHT_PHASE_STEPS[phase.step] ?? null;
  }
  return null;
}

export function routeProgressFrac(route?: FlightProgressRoute | null): number | null {
  const total = route?.totalNm;
  const remaining = route?.remainingNm;
  if (typeof total !== "number" || !Number.isFinite(total) || total < 2) return null;
  if (typeof remaining !== "number" || !Number.isFinite(remaining)) return null;
  return clamp01(1 - remaining / total);
}

/**
 * Covered vs remaining along the filed route (0–1). Independent of phase
 * bands — early cruise stays near 0, not parked on the climb tick.
 * Idle / no route is 0. Arrived without a remaining figure finishes at 1.
 */
export function flightDistanceTrackT(
  phase: Pick<FlightPhaseView, "fromSim"> & { id?: FlightPhaseId },
  route?: FlightProgressRoute | null,
): number {
  if (!phase.fromSim) return 0;
  const flown = routeProgressFrac(route);
  if (flown != null) return flown;
  if (phase.id && phaseIsArrived(phase.id)) return 1;
  return 0;
}

function bandProgress(id: (typeof FLIGHT_PHASE_STEPS)[number], fraction: number): number {
  const band = PHASE_PROGRESS_BANDS[id];
  return band.start + clamp01(fraction) * (band.end - band.start);
}

/**
 * 0–1 position along the ops-strip progress track. Idle / unknown sits at
 * the start. Ground-out and Ground-in use kinematics inside their bands.
 * Airborne prefers remaining/total NM so cruise is a continuous slide,
 * not a parked tick. `parked` (post-landing Ground) finishes at 1.
 */
export function flightPhaseTrackT(
  phase: Pick<FlightPhaseView, "step" | "total" | "fromSim"> & { fraction?: number; id?: FlightPhaseId },
  fraction?: number,
  route?: FlightProgressRoute,
): number {
  if (!phase.fromSim || phase.step < 0 || phase.total <= 1) return 0;
  const id = phaseIdFromTrack(phase);
  if (!id) return 0;
  const raw = fraction ?? phase.fraction ?? 0;
  const frac = Number.isFinite(raw) ? Math.min(1, Math.max(0, raw)) : 0;
  const airborne = phaseIsAirborne(id);
  const flown = airborne ? routeProgressFrac(route) : null;
  if (flown != null) {
    return clamp01(PHASE_AIR_PROGRESS.start + flown * (PHASE_AIR_PROGRESS.end - PHASE_AIR_PROGRESS.start));
  }
  return bandProgress(id, frac);
}

/**
 * Plane mark for the current step — desk #89: do not stack a diamond under it.
 */
export function phaseBarShowStepMark(live: boolean, stepIndex: number, currentStep: number): boolean {
  if (!live) return true;
  return stepIndex !== currentStep;
}

/** Equal chrome ticks (labels stay a 9-col grid). Plane position is independent. */
export function phaseBarTickLeft(stepIndex: number, total = FLIGHT_PHASE_STEPS.length): number {
  if (total <= 1) return 0;
  return clamp01(stepIndex / (total - 1));
}

/**
 * Keep the plane moving left→right during a flight. A new departure
 * (ground-out or the next takeoff/climb after Ground-in) may reset.
 */
export function clampFlightPhaseTrackT(
  previous: number,
  next: number,
  phase: FlightPhaseId,
  fromSim: boolean,
): number {
  if (!fromSim) return 0;
  const prev = clamp01(previous);
  const nxt = clamp01(next);
  const restart =
    prev >= 0.78 &&
    nxt <= 0.32 &&
    (phase === "on_ground" || phase === "taxi" || phase === "takeoff" || phase === "climb");
  if (restart) return nxt;
  return Math.max(prev, nxt);
}

export type SimConnectLink = {
  enabled?: boolean;
  error?: string;
};

/** Short strip line when the native client DLL failed to load. The Win32 code is appended when the poll recorded one. */
export const SIMCONNECT_DLL_STRIP_NOTE =
  "SimConnect.dll failed to load. This is not MSFS being closed. Install the MSFS 2024 SDK (Dev Mode → Help → SDK Installer) or copy the x64 SimConnect.dll next to the desk .exe.";

export function simconnectDllNote(error?: string): string {
  const code = /Win32 error (\d+)/.exec(error ?? "");
  if (!code) return SIMCONNECT_DLL_STRIP_NOTE;
  return `${SIMCONNECT_DLL_STRIP_NOTE} Win32 error ${code[1]}.`;
}

export function isSimConnectDllError(error?: string): boolean {
  const err = error?.trim() ?? "";
  if (!err) return false;
  return /SimConnect\.dll/i.test(err) && /could not load|failed to load|LoadLibrary|missing/i.test(err);
}

function firstSimConnectClause(error: string): string {
  return error.split(/ HTTP fallback/i)[0]?.trim() || error;
}

/** Desk copy when phase is not live SimConnect. Never silent Unknown. */
export function simconnectWaitNote(input: SimConnectLink = {}): string {
  if (input.enabled === false) {
    return "SimConnect off / waiting for MSFS. Enable SimConnect in Settings and start MSFS 2024 on this PC.";
  }
  const err = input.error?.trim() ?? "";
  if (isSimConnectDllError(err)) return simconnectDllNote(err);
  if (/did not accept a connection/i.test(err)) {
    return "MSFS is not running on this PC. Start Microsoft Flight Simulator 2024. The SimConnect DLL load is a separate fault.";
  }
  if (/waiting to retry/i.test(err)) {
    const code = /Win32 error (\d+)/.exec(err);
    if (code) return `SimConnect is waiting to retry. DLL load failed. Win32 error ${code[1]}. This is not MSFS being closed.`;
    if (/not running|did not accept a connection/i.test(err)) {
      return "SimConnect is waiting to retry. MSFS is not running on this PC.";
    }
    return "SimConnect is waiting to retry. The desk keeps one session and does not reopen every second.";
  }
  if (/Windows-only/i.test(err)) {
    return "Native SimConnect is Windows-only. Use the desk .exe on the sim PC.";
  }
  if (err) {
    const first = firstSimConnectClause(err);
    if (first.length <= 160) return first;
    return "Waiting for MSFS. Check Settings for the SimConnect error.";
  }
  return "Waiting for MSFS. No live SimConnect sample on this PC.";
}

function absNum(value: number | null | undefined): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return Math.abs(value);
}

/** Groundspeed preferred; indicated airspeed if GS is missing. */
export function sampleSpeedKt(sample: Pick<PositionSample, "groundSpeed" | "airspeed">): number | null {
  return absNum(sample.groundSpeed) ?? absNum(sample.airspeed);
}

/** Level band for gear-based climb / cruise / descent. */
export const GEAR_LEVEL_FPM = 150;

function gearAirbornePhase(sample: PositionSample, previous?: FlightPhaseId | null): FlightPhaseView {
  const gearDown = sample.gearDown === true;
  const vs =
    typeof sample.verticalSpeedFpm === "number" && Number.isFinite(sample.verticalSpeedFpm)
      ? sample.verticalSpeedFpm
      : null;
  const agl =
    typeof sample.altitudeAgl === "number" && Number.isFinite(sample.altitudeAgl) ? sample.altitudeAgl : null;
  const climbing = vs != null && vs > GEAR_LEVEL_FPM;
  const descending = vs != null && vs < -GEAR_LEVEL_FPM;
  const fromSurface =
    previous === "takeoff" || previous === "taxi" || previous === "on_ground" || previous === "parked";
  const freshDeparture =
    fromSurface || ((previous == null || previous === "unknown") && agl != null && agl < 1500 && climbing);
  if (freshDeparture) {
    return view("climb", true, gearDown ? "Airborne — climb." : "Gear up — departure.", sample);
  }
  if (
    gearDown &&
    (previous === "cruise" || previous === "descent" || previous === "approach" || previous === "landed")
  ) {
    return view("approach", true, "Gear down — approach.", sample);
  }
  if (previous === "climb" && gearDown && (vs == null || vs > 0)) {
    return view("climb", true, "Climb with gear down.", sample);
  }
  if (previous === "climb" && gearDown) {
    return view("approach", true, "Gear down — approach.", sample);
  }
  if (previous === "approach" && !gearDown && climbing) {
    return view("climb", true, "Gear up — go-around.", sample);
  }
  if (climbing) return view("climb", true, "Positive vertical speed.", sample);
  if (descending) return view("descent", true, "Descending (VS / AGL).", sample);
  return view("cruise", true, "Airborne, not in a climb or descent.", sample);
}

function wasAirborne(previous?: FlightPhaseId | null): boolean {
  return Boolean(previous && AIRBORNE.has(previous));
}

/** Climb/cruise/descent/approach — not takeoff roll, so an RTO stays departure-side. */
function isArrivalAirborne(previous?: FlightPhaseId | null): boolean {
  return previous === "climb" || previous === "cruise" || previous === "descent" || previous === "approach";
}

function deriveFromSample(sample: PositionSample, previous?: FlightPhaseId | null): FlightPhaseView {
  const speed = sampleSpeedKt(sample);
  const vs = typeof sample.verticalSpeedFpm === "number" && Number.isFinite(sample.verticalSpeedFpm)
    ? sample.verticalSpeedFpm
    : null;
  const agl = typeof sample.altitudeAgl === "number" && Number.isFinite(sample.altitudeAgl)
    ? sample.altitudeAgl
    : null;

  let onGround = sample.onGround;
  if (onGround == null && agl != null && speed != null && agl < 8 && speed < 35) onGround = true;
  if (onGround == null && agl != null && agl > 80) onGround = false;

  if (onGround == null && agl == null && vs == null && speed == null) {
    return view("unknown", true, "SimConnect sample has lat/lon but no on-ground / AGL / speed.", sample);
  }

  if (onGround === true) {
    if (previous === "parked") {
      if (speed != null && speed >= 55) {
        return view("takeoff", true, "On ground at high speed after Ground-in — next departure.", sample);
      }
      return view("parked", true, "Taxi-in / parked after landing.", sample);
    }
    if (previous === "landed") {
      if (speed != null && speed >= 40) {
        return view("landed", true, "Landing rollout.", sample);
      }
      return view(
        "parked",
        true,
        speed != null && speed >= 8 ? "Taxi-in after landing." : "Parked after landing.",
        sample,
      );
    }
    if (isArrivalAirborne(previous)) {
      return view("landed", true, "On ground after an airborne / landing sample.", sample);
    }
    if (speed != null && speed >= 55) {
      return view("takeoff", true, "On ground at high speed.", sample);
    }
    if (speed != null && speed >= (wasAirborne(previous) ? AFTER_AIRBORNE_TAXI_KT : GROUND_MOVING_KT)) {
      return view("taxi", true, "On ground and moving.", sample);
    }
    if (wasAirborne(previous)) {
      return view("landed", true, "On ground after an airborne / landing sample.", sample);
    }
    return view("on_ground", true, "On ground and stopped.", sample);
  }

  if (onGround === false || (agl != null && agl >= 80)) {
    if (typeof sample.gearDown === "boolean") return gearAirbornePhase(sample, previous);
    const arriving =
      previous === "descent" || previous === "approach" || previous === "cruise" || previous === "landed";
    const departing =
      previous === "takeoff" || previous === "climb" || previous === "taxi" || previous === "on_ground";

    if (agl != null && agl < 400 && (vs == null || vs > -200) && (speed == null || speed >= 50)) {
      if (previous === "approach" && (vs == null || vs <= 0)) {
        return view("approach", true, "Low AGL, still arriving.", sample);
      }
      return view("takeoff", true, "Airborne at low AGL.", sample);
    }
    if (vs != null && vs < -400 && agl != null && agl > 2500) {
      if (previous === "cruise" && vs > -550) {
        return view("cruise", true, "Airborne, not in a climb or descent.", sample);
      }
      return view("descent", true, "Descending (VS / AGL).", sample);
    }
    if (vs != null && vs < -200 && (agl == null || agl <= 2500)) {
      if (departing) {
        if (vs > 300) {
          return agl != null && agl < 1200
            ? view("takeoff", true, "Climbing through low AGL.", sample)
            : view("climb", true, "Positive vertical speed.", sample);
        }
        return previous === "takeoff"
          ? view("takeoff", true, "Airborne at low AGL.", sample)
          : view("climb", true, "Positive vertical speed.", sample);
      }
      return view("approach", true, "Descending at low / medium AGL.", sample);
    }
    if (agl != null && agl <= 1800 && (vs == null || vs <= 100)) {
      if (arriving) {
        return view("approach", true, "Low AGL after cruise / descent.", sample);
      }
      if (departing) {
        if (vs != null && vs > 300) {
          return agl < 1200
            ? view("takeoff", true, "Climbing through low AGL.", sample)
            : view("climb", true, "Positive vertical speed.", sample);
        }
        return previous === "takeoff" || previous === "taxi" || previous === "on_ground"
          ? view("takeoff", true, "Airborne at low AGL.", sample)
          : view("climb", true, "Positive vertical speed.", sample);
      }
      return view("approach", true, "Low AGL after departure / cruise.", sample);
    }
    if (vs != null && vs > 300) {
      if (previous === "cruise" && vs < 600) {
        return view("cruise", true, "Airborne, not in a climb or descent.", sample);
      }
      if (previous === "descent" && vs < DESCENT_LEVELOFF_CLIMB_FPM) {
        return view("descent", true, "Descent held through a level-off / step.", sample);
      }
      if (agl != null && agl < 1200) return view("takeoff", true, "Climbing through low AGL.", sample);
      return view("climb", true, "Positive vertical speed.", sample);
    }
    if (previous === "descent" && (vs == null || vs < DESCENT_LEVELOFF_CLIMB_FPM)) {
      return view("descent", true, "Descent held through a level-off / step.", sample);
    }
    return view("cruise", true, "Airborne, not in a climb or descent.", sample);
  }

  return view("unknown", true, "SimConnect sample is inconclusive for phase.", sample);
}

/**
 * Phase from fused position. Live SimConnect only — SI / last-known never
 * produce taxi/cruise. Optional `previous` distinguishes departure ground
 * from post-landing Ground (`parked`).
 */
/**
 * When remaining-to-dest is within ~10 NM, the status bar shows Approach.
 * Does not invent approach from takeoff/climb or any on-ground phase.
 */
export function applyApproachOverride(
  phase: FlightPhaseView,
  destRemainingNm?: number | null,
  sample?: PositionSample,
): FlightPhaseView {
  if (!phase.fromSim) return phase;
  if (destRemainingNm == null || !Number.isFinite(destRemainingNm) || destRemainingNm < 0) return phase;
  if (destRemainingNm > APPROACH_OVERRIDE_NM) return phase;
  if (phaseIsOnSurface(phase.id) || phase.id === "takeoff" || phase.id === "climb") return phase;
  if (phase.id === "approach") return phase;
  return view("approach", true, "Within 10 NM of arrival.", sample);
}

export function deriveFlightPhase(input: {
  fused?: FusedPosition | null;
  previous?: FlightPhaseId | null;
  now?: Date;
  simconnectEnabled?: boolean;
  simconnectError?: string;
  destRemainingNm?: number | null;
}): FlightPhaseView {
  const fused = input.fused;
  const link: SimConnectLink = { enabled: input.simconnectEnabled, error: input.simconnectError };
  const wait = simconnectWaitNote(link);
  if (!fused) {
    return view("no_sim", false, wait);
  }
  if (fused.source !== "simconnect" || fused.stale || !isFreshSample(fused, input.now)) {
    if (fused.source === "last_known") {
      return view("unknown", false, `${wait} Last-known position only — no live SimConnect phase.`);
    }
    if (fused.source === "sayintentions") {
      return view("unknown", false, `${wait} SI position only — phase needs a live SimConnect sample.`);
    }
    if (fused.source === "vatsim") {
      return view("unknown", false, `${wait} VATSIM position only — phase needs a live SimConnect sample.`);
    }
    return view("unknown", false, `${wait} SimConnect sample is stale. Phase is unknown.`);
  }
  const phase = deriveFromSample(fused, input.previous);
  if (typeof fused.gearDown === "boolean") return phase;
  return applyApproachOverride(phase, input.destRemainingNm, fused);
}

/**
 * Shared contract for assignment / desk. Always returns a JSON-safe object.
 * `live` is true only for a fresh SimConnect sample with a derived phase.
 */
export function aircraftSimState(input: {
  fused?: FusedPosition | null;
  previous?: FlightPhaseId | null;
  now?: Date;
  simconnectEnabled?: boolean;
  simconnectError?: string;
  destRemainingNm?: number | null;
}): AircraftSimState {
  const phase = deriveFlightPhase(input);
  const fused = input.fused ?? null;
  const live = phase.fromSim;
  return {
    phase: phase.id,
    live,
    source: fused?.source ?? null,
    sample: live && fused
      ? {
          lat: fused.lat,
          lon: fused.lon,
          heading: fused.heading,
          altitudeFt: fused.altitudeFt,
          altitudeAgl: fused.altitudeAgl,
          airspeed: fused.airspeed,
          groundSpeed: fused.groundSpeed,
          verticalSpeedFpm: fused.verticalSpeedFpm,
          onGround: fused.onGround,
          atUtc: fused.atUtc,
          squawk: fused.squawk ?? null,
          trackDeg: fused.trackDeg ?? null,
          fuelQtyLb: fused.fuelQtyLb ?? null,
          gearDown: typeof fused.gearDown === "boolean" ? fused.gearDown : null,
        }
      : null,
    atUtc: fused?.atUtc ?? null,
    note: phase.note,
  };
}
